import { useCallback } from "react";
import DOMPurify from "dompurify";

/**
 * Configures DOMPurify with strong XSS protections.
 * It removes:
 * - <script>, <iframe>, <svg>, <math>, etc.
 * - on* event handlers
 * - javascript:, data:, vbscript:
 * - srcdoc, inline styles with expressions, atob, fromCharCode
 * - template injections like {{...}}, ${...}
 */
export function sanitizeInput(html) {
  try {
    html = decodeURIComponent(html); // Decode URI payloads
  } catch {}

  // Sanitize using DOMPurify with strong config
  let clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: false, // Disallow all tags unless explicitly allowed
    ALLOWED_ATTR: false, // Disallow all attributes unless explicitly allowed
    FORBID_TAGS: ["script", "iframe", "svg", "math"], // Extra enforcement
    FORBID_ATTR: ["style", "onerror", "onload", "onclick", "srcdoc"], // Dangerous attributes
    FORCE_BODY: true, // Ensure valid wrapping
  });

  // Manual cleanup (edge cases)
  clean = clean
    .replace(/\{\{.*?\}\}/g, "") // Remove {{...}} interpolation
    .replace(/\$\{.*?\}/g, "") // Remove ${...} template strings
    .replace(/expression\s*\(/gi, "") // Remove CSS `expression()`
    .replace(/(javascript:|vbscript:|data:text\/html)/gi, ""); // Remove URI-based attacks

  return clean;
}

export function newsanitizeInput(html) {
  if (!html) return "";

  try {
    html = decodeURIComponent(html);
  } catch {}

  let clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "strong", "em", "a", "ul", "ol", "li", "br", "img"],

    ALLOWED_ATTR: ["href", "src", "alt"],

    FORBID_TAGS: ["script", "iframe", "svg", "math", "object", "embed"],

    FORBID_ATTR: ["style", "onerror", "onload", "onclick", "onmouseover", "onfocus", "srcdoc"],

    FORCE_BODY: true,
  });

  // Remove template injection patterns
  clean = clean
    .replace(/\{\{.*?\}\}/g, "")
    .replace(/\$\{.*?\}/g, "")
    .replace(/expression\s*\(/gi, "")
    .replace(/(javascript:|vbscript:|data:text\/html)/gi, "");

  return clean;
}

/**
 * Custom React hook to sanitize input using DOMPurify
 */
export default function useSanitizedContent(setterFn, logger = null, useNewSanitized = false) {
  return useCallback(
    (rawHtml) => {
      const cleaned = useNewSanitized ? newsanitizeInput(rawHtml) : sanitizeInput(rawHtml);
      if (logger) logger(`Sanitized: ${cleaned}`);
      setterFn(cleaned);
    },
    [setterFn, logger]
  );
}
